import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { themes, themeOrder, defaultThemeOrder, defaultTheme, normalizeThemeName, applyTheme } from '../themes';

const THEME_STORAGE_KEY = 'lumoz-theme';

const ThemeContext = createContext(null);

const getNextThemeName = (currentThemeName, order) => {
  const currentIndex = order.indexOf(currentThemeName);
  return order[(currentIndex + 1) % order.length] || order[0] || defaultTheme;
};

const getNextDefaultThemeName = (currentThemeName) => {
  return themes[currentThemeName]?.mode === 'dark' ? 'light' : 'dark';
};

/**
 * ThemeProvider - Manages application theme
 *
 * Features:
 * - Persists theme preference to localStorage
 * - Applies theme via CSS custom properties
 * - Sets data-theme attribute on document element
 * - Defaults to dark mode (current design)
 */
export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage or default
  const [themeName, setThemeName] = useState(() => {
    if (typeof window === 'undefined') return defaultTheme;
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    const normalized = normalizeThemeName(stored);
    return normalized && themes[normalized] ? normalized : defaultTheme;
  });

  // Get current theme object
  const theme = useMemo(() => themes[themeName], [themeName]);

  // Apply theme to document when it changes
  useEffect(() => {
    if (theme) {
      applyTheme(theme);
      localStorage.setItem(THEME_STORAGE_KEY, themeName);
    }
  }, [theme, themeName]);

  const setTheme = useCallback((newThemeName) => {
    const normalized = normalizeThemeName(newThemeName);
    if (themes[normalized]) {
      setThemeName(normalized);
    } else {
      console.warn(`Theme "${newThemeName}" not found. Available themes:`, Object.keys(themes));
    }
  }, []);

  const toggleTheme = useCallback(({ includeAll = false } = {}) => {
    setThemeName(prev => {
      return includeAll
        ? getNextThemeName(prev, themeOrder)
        : getNextDefaultThemeName(prev);
    });
  }, []);

  const isDark = theme?.mode === 'dark';
  const isLight = theme?.mode === 'light';
  const nextDefaultThemeName = getNextDefaultThemeName(themeName);
  const nextDefaultTheme = themes[nextDefaultThemeName];
  const nextThemeName = getNextThemeName(themeName, themeOrder);
  const nextTheme = themes[nextThemeName];

  const value = useMemo(() => ({
    theme,
    themeName,
    setTheme,
    toggleTheme,
    isDark,
    isLight,
    nextDefaultTheme,
    nextTheme,
    availableThemes: themeOrder,
    defaultThemes: defaultThemeOrder,
  }), [theme, themeName, setTheme, toggleTheme, isDark, isLight, nextDefaultTheme, nextTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
